import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Package, Plus } from 'lucide-react'
import { PAYMENT_FREQUENCY_LABELS } from '@tumin/domain'
import { DataTable, type Column } from '@/components/data-table'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { useProducts } from '@/features/admin/products/api'
import type { CreditProduct } from '@/features/admin/products/types'
import { SIZES } from '@/lib/constants'
import { formatValue } from '@/lib/format'
import { ROUTES } from '@/lib/routes'
import { requirePermission } from '@/lib/route-guards'
import { useShortcuts, type ShortcutDef } from '@/lib/shortcuts'

export const Route = createFileRoute('/_app/admin/products/')({
  beforeLoad: requirePermission('catalog:manage'),
  component: ProductsPage,
})

function rangeText(min: number, max: number, format: 'money' | 'basisPoints'): string {
  return `${formatValue(min, format)} – ${formatValue(max, format)}`
}

const COLUMNS: Column<CreditProduct>[] = [
  {
    id: 'name',
    header: 'Producto',
    cell: (p) => (
      <div className="min-w-0">
        <p className="font-medium">{p.name}</p>
        <p className="tabular text-xs text-muted-foreground">{p.code}</p>
      </div>
    ),
  },
  { id: 'amount', header: 'Monto', align: 'right', className: 'tabular whitespace-nowrap', cell: (p) => rangeText(p.minAmount, p.maxAmount, 'money') },
  { id: 'rate', header: 'Tasa anual', align: 'right', className: 'tabular whitespace-nowrap', cell: (p) => rangeText(p.minAnnualRate, p.maxAnnualRate, 'basisPoints') },
  { id: 'term', header: 'Plazo', align: 'right', className: 'tabular', cell: (p) => `${String(p.minTermPeriods)}–${String(p.maxTermPeriods)} cuotas` },
  { id: 'frequencies', header: 'Frecuencias', hideOnMobile: true, cell: (p) => p.frequencies.map((f) => PAYMENT_FREQUENCY_LABELS[f]).join(', ') },
  { id: 'status', header: 'Estado', cell: (p) => <StatusBadge kind="active" value={String(p.isActive)} /> },
]

/** Normaliza texto para buscar sin tildes. */
function normalize(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

function ProductsPage() {
  const navigate = useNavigate()
  const { data, isLoading, error, refetch } = useProducts()
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)
  const pageSize = SIZES.defaultPageSize
  // El catálogo es pequeño y el API lo devuelve completo: se filtra en el navegador.
  const filtered = useMemo(() => {
    const q = normalize(query.trim())
    return (data ?? []).filter((p) => !q || normalize(`${p.name} ${p.code}`).includes(q))
  }, [data, query])
  const rows = filtered.slice(page * pageSize, (page + 1) * pageSize)

  const shortcuts = useMemo<ShortcutDef[]>(
    () => [{ id: 'product-new', group: 'Acciones', keys: ['N'], sequence: ['n'], label: 'Nuevo producto', run: () => void navigate({ to: ROUTES.newProduct }) }],
    [navigate],
  )
  useShortcuts(shortcuts)

  const newButton = (
    <Button asChild>
      <Link to={ROUTES.newProduct}>
        <Plus /> Nuevo producto
      </Link>
    </Button>
  )

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Administración" title="Productos de crédito" description="Montos, plazos, tasas, cargos y garantías de cada producto." actions={newButton} />
      <DataTable
        ariaLabel="Productos de crédito"
        columns={COLUMNS}
        rows={rows}
        total={filtered.length}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        query={query}
        onQueryChange={(q) => {
          setQuery(q)
          setPage(0)
        }}
        searchPlaceholder="Buscar por nombre o código…"
        getRowId={(p) => p.id}
        isLoading={isLoading}
        error={error}
        onRetry={() => void refetch()}
        onRowClick={(p) => void navigate({ to: '/admin/products/$productId', params: { productId: p.id } })}
        emptyTitle="Aún no hay productos"
        emptyHint="Cree el primer producto para poder registrar solicitudes de crédito."
        emptyAction={newButton}
        mobileCard={(p) => (
          <div className="space-y-1">
            <div className="flex items-start justify-between gap-2">
              <p className="font-medium">{p.name}</p>
              <StatusBadge kind="active" value={String(p.isActive)} />
            </div>
            <p className="tabular text-xs text-muted-foreground">
              {rangeText(p.minAmount, p.maxAmount, 'money')} · {rangeText(p.minAnnualRate, p.maxAnnualRate, 'basisPoints')}
            </p>
          </div>
        )}
      />
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Package className="size-3.5" aria-hidden /> Editar un producto no cambia los préstamos ya otorgados: cada préstamo conserva sus condiciones.
      </p>
    </div>
  )
}
